import { Injector, computed, runInInjectionContext, signal } from '@angular/core';
import { emptyChartOpts } from '@features/social-analysis/components/chart/config/chart-empty-opts';
import { IData } from '@core/models/IApi';
import { ChartOpts } from '@features/social-analysis/models/IChart';
import { DimCategory, Dimension } from '@features/social-analysis/models/IDimensions';
import { Components } from '@features/social-analysis/social-listening/config/page';
import { InsightsChartHttpBuilder } from '../../social-listening/services/ins-chart.http';
import { PostChartHttpBuilder } from '../../social-listening/services/posts-chart.http';
import { UsersPostChartHttpBuilder } from '../../social-users/services/post-chart.http';
import { PageFiltersState } from './page-filters.store';
import { SingleSelectState } from './state.store';

type ChartInit = {
    component: Components;
    dimOpts: DimCategory[];
    metricOpts: DimCategory[];
    maxMetrics: number;
    dimension: Dimension[];
    metrics: Dimension[];
};

export abstract class ChartState<T = IData, K = T> extends SingleSelectState<T, K> {
    component = '';
    dimOpts;
    metricOpts;
    maxMetrics;
    dimensions = signal<Dimension[]>([]);
    selectableDims = computed(() => this.dimensions().filter(dim => !dim.fixed));
    metrics = signal<Dimension[]>([]);
    computedData = signal<ChartOpts>(structuredClone(emptyChartOpts));

    constructor({ component, dimOpts, metricOpts, maxMetrics, dimension, metrics }: ChartInit) {
        super();
        this.component = component;
        this.dimOpts = dimOpts;
        this.metricOpts = metricOpts;
        this.maxMetrics = maxMetrics;
        this.dimensions.set(dimension);
        this.metrics.set(metrics);
    }

    setDimensions(dims: Dimension[]) {
        this.dimensions.set(dims);
    }

    setMetrics(metrics: Dimension[]) {
        this.metrics.set(metrics);
    }
}

export class PostChart extends ChartState {
    #http!: PostChartHttpBuilder;

    constructor(init: ChartInit, injector: Injector) {
        super(init);
        runInInjectionContext(injector, () => (this.#http = new PostChartHttpBuilder()));
    }

    reqData(pageFilters: PageFiltersState, postId: string) {
        this.#http.reqData(this, pageFilters, postId);
    }
}

export class UsersPostChart extends ChartState {
    #http!: UsersPostChartHttpBuilder;

    constructor(init: ChartInit, injector: Injector) {
        super(init);
        runInInjectionContext(injector, () => (this.#http = new UsersPostChartHttpBuilder()));
    }

    reqData(pageFilters: PageFiltersState, postId: string) {
        this.#http.reqData(this, pageFilters, postId);
    }
}

export class SocListnInsChart extends ChartState<IData[]> {
    #http!: InsightsChartHttpBuilder;
    fixedDim: Dimension | null = null;
    series = signal<{ display: string; visible: boolean }[]>([]);

    constructor(
        {
            component,
            dimOpts,
            metricOpts,
            fixedDim,
            maxMetrics,
            dimension,
            metrics,
        }: ChartInit & { fixedDim: Dimension },
        injector: Injector
    ) {
        super({ component, dimOpts, metricOpts, maxMetrics, dimension, metrics });
        this.fixedDim = fixedDim;
        runInInjectionContext(injector, () => (this.#http = new InsightsChartHttpBuilder()));
    }

    override setDimensions(dims: Dimension[]) {
        if (this.fixedDim) dims.unshift(this.fixedDim);
        super.setDimensions(dims);
    }

    reqData(pageFilters: PageFiltersState) {
        this.#http.reqData(this, pageFilters);
    }

    // getSeriesNames() {
    //     const field = IgAccount.getPrefixed('igAccount');
    //     return this.data().map(grouped => (grouped[0]?.[field] as string) || '');
    // }
}
